import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  Stack,
  Switch,
  FormControlLabel,
  IconButton,
  Paper,
  Avatar,
  Chip,
  Tooltip,
  Grid,
  Divider,
  Button
} from '@mui/material';
import { fleetApi } from '../../../api/axiosClient';
import { NavigateBefore, NavigateNext } from '@mui/icons-material';
import { useLanguage } from '../../../utils/i18n';
import { getVehicleImage, getVehicleBarcodeImage } from '../../../stores/masterVehicleStore';
import DetailDialog from '../../../components/DetailDialog';
import { getVehicleLicenseDetailFile } from '../../../stores/vehicleLicenseDetailStore';
import { CloudDownload } from '@mui/icons-material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import StorageIcon from '@mui/icons-material/Storage';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FuelInformationDialog from './form/FuelInformationDialog';
import { getMasterFuelPriceById } from '../../../stores/masterFuelPriceStore';
import { SPACES } from '../../../config/constants';

export default function MasterVehicleDetail({ vehicle: propVehicle = null }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { id: idFromParams } = useParams();
  const idFromQuery = searchParams.get('id') ?? null;
  const { t } = useLanguage();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fuelTypeId, setFuelTypeId] = useState('');

  const [vehiclePreview, setVehiclePreview] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [qrPreview, setQrPreview] = useState(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailContent, setDetailContent] = useState({ type: 'photo', front: '', back: '', document: '', label: '' });
  const [fuelHistoryDialogOpen, setFuelHistoryDialogOpen] = useState(false);

  const getImageUrl = (item) => {
    if (item.id) {
      const token = localStorage.getItem('apiKey');
      const vehicleId = vehicle?.id;
      return `${SPACES.FMS_ITI_SPACES}/oslog/prod/images/vehicle-license/${vehicleId}/${item.id}_${vehicleId}_front.jpeg?apikey=${token}`;
    }
    return null;
  };

  const handleOpenDetail = async (item) => {
    const label = item.join?.vehicle_license_license_name || item.license_name || item.license_type_label || 'License Detail';
    setDetailOpen(true);
    setDetailContent((prev) => ({ ...prev, label }));

    if (item.id) {
      try {
        const [frontRes, backRes, docRes] = await Promise.allSettled([
          getVehicleLicenseDetailFile(item.id, 'front'),
          getVehicleLicenseDetailFile(item.id, 'back'),
          getVehicleLicenseDetailFile(item.id, 'document')
        ]);

        const front = frontRes.status === 'fulfilled' ? frontRes.value : '';
        const back = backRes.status === 'fulfilled' ? backRes.value : '';
        const doc = docRes.status === 'fulfilled' ? docRes.value : '';

        setDetailContent({
          label,
          front,
          back,
          document: doc,
          type: front || back ? 'photo' : doc ? 'document' : 'photo'
        });
      } catch (err) {
        console.error('Preview failed', err);
      }
    }
  };

  const handleGetImages = async (id) => {
    try {
      const response = await getVehicleImage(id);
      let images = [];
      if (typeof response === 'string' && response !== '') {
        images.push(response);
      } else if (Array.isArray(response)) {
        images = response;
      }
      setVehiclePreview(images);

      const qr = await getVehicleBarcodeImage(id);
      if (qr) setQrPreview(qr);
    } catch (err) {
      console.error('Failed to load images', err);
    }
  };

  useEffect(() => {
    const idToFetch = idFromParams ?? idFromQuery;

    if (propVehicle) {
      setVehicle(propVehicle);
      setLoading(false);
      if (propVehicle.id) handleGetImages(propVehicle.id);
    } else if (idToFetch) {
      const fetchDetail = async () => {
        setLoading(true);
        try {
          const resp = await fleetApi.get(`/vehicle/${encodeURIComponent(idToFetch)}`);
          const data = resp.data?.data ?? resp.data;
          setVehicle(data);
          handleGetImages(idToFetch);
        } catch (err) {
          console.error('fetch vehicle detail error', err);
        } finally {
          setLoading(false);
        }
      };
      fetchDetail();
    }
  }, [propVehicle, idFromParams, idFromQuery]);

  useEffect(() => {
    if (!vehicle) return;
    const extractedId =
      (typeof vehicle.fuel_type === 'object' ? vehicle.fuel_type?.id : vehicle.fuel_type) ||
      vehicle.fuel_type_id ||
      vehicle?.fuel_type_join?.id ||
      vehicle?.join?.fuel_type_id ||
      vehicle?.join?.fuel_type ||
      vehicle?.master_fuel_price_join?.fuel_type_id ||
      vehicle?.master_fuel_price_join?.fuel_type ||
      vehicle?.fuel_price_join?.fuel_type_id ||
      vehicle?.fuel_price_join?.fuel_type ||
      '';

    if (extractedId && String(extractedId) !== '0') {
      setFuelTypeId(String(extractedId));
    } else {
      const fuelPriceId = vehicle.fuel_price || vehicle.fuel_price_id || '-';
      if (fuelPriceId && String(fuelPriceId) !== '0') {
        getMasterFuelPriceById(fuelPriceId)
          .then((fp) => {
            const fpData = fp?.data ?? fp;
            const fpFuelTypeId =
              (typeof fpData?.fuel_type === 'object' ? fpData?.fuel_type?.id : fpData?.fuel_type) ||
              fpData?.fuel_type_id ||
              fpData?.fuel_type_join?.id ||
              fpData?.join?.fuel_type_id ||
              '';
            if (fpFuelTypeId) setFuelTypeId(String(fpFuelTypeId));
          })
          .catch(() => {});
      } else {
        setFuelTypeId('');
      }
    }
  }, [vehicle]);

  if (loading) {
    return (
      <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!vehicle) {
    return (
      <Box sx={{ p: 6 }}>
        <Typography variant="h6">{t('masterVehicle.textHelpVehicleDetail', 'Vehicle details not available')}</Typography>
        <Button onClick={() => navigate(-1)}>{t('buttonTitle.back', 'Back')}</Button>
      </Box>
    );
  }

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: '#f5f5f5',
      '& fieldset': { borderColor: '#e0e0e0' }
    },
    '& .MuiInputLabel-root': { color: 'text.secondary', fontSize: 13 }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 4,
          alignItems: 'flex-start',
          width: '100%'
        }}
      >
        <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 30%' } }}>
          <Box
            sx={{
              width: '100%',
              height: { xs: 300, md: 400 },
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              bgcolor: 'grey.100',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            {vehiclePreview.length > 0 ? (
              <>
                <Box
                  component="img"
                  src={vehiclePreview[currentImageIndex]}
                  alt="vehicle"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                {vehiclePreview.length > 1 && (
                  <>
                    <IconButton
                      onClick={() => setCurrentImageIndex((prev) => Math.max(prev - 1, 0))}
                      disabled={currentImageIndex === 0}
                      sx={{ position: 'absolute', left: 8, bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: '#fff' } }}
                    >
                      <NavigateBefore />
                    </IconButton>
                    <IconButton
                      onClick={() => setCurrentImageIndex((prev) => Math.min(prev + 1, vehiclePreview.length - 1))}
                      disabled={currentImageIndex === vehiclePreview.length - 1}
                      sx={{ position: 'absolute', right: 8, bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: '#fff' } }}
                    >
                      <NavigateNext />
                    </IconButton>
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        bgcolor: 'rgba(0,0,0,0.5)',
                        color: '#fff',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 10,
                        fontSize: 12
                      }}
                    >
                      {currentImageIndex + 1} / {vehiclePreview.length}
                    </Box>
                  </>
                )}
              </>
            ) : (
              <Typography color="text.secondary">{t('masterVehicle.photoNotAVaible', 'No Photos')}</Typography>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            flex: { xs: '1 1 100%', md: '0 0 60%' },
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4
          }}
        >
          <Box sx={{ flex: '1 1 40%', minWidth: 200 }}>
            <Stack spacing={2}>
              <TextField
                label={t('general.company', 'Company')}
                value={vehicle.join?.company_name ?? vehicle.company_name ?? '-'}
                fullWidth
                variant="outlined"
                InputProps={{ readOnly: true }}
                sx={fieldSx}
              />
              <TextField
                label={t('general.vehicleType', 'Vehicle Type')}
                value={vehicle.vehicle_type_name ?? '-'}
                fullWidth
                variant="outlined"
                InputProps={{ readOnly: true }}
                sx={fieldSx}
              />
              <TextField
                label={t('masterVehicle.bodyName', 'Body Name')}
                value={vehicle.name ?? '-'}
                fullWidth
                variant="outlined"
                InputProps={{ readOnly: true }}
                sx={fieldSx}
              />
              <TextField
                label={t('masterVehicle.plateNumber', 'Plate Number')}
                value={vehicle.license_plate ?? '-'}
                fullWidth
                variant="outlined"
                InputProps={{ readOnly: true }}
                sx={fieldSx}
              />
              <TextField
                label={t('general.merk', 'Merk')}
                value={vehicle.merk ?? vehicle.make ?? '-'}
                fullWidth
                variant="outlined"
                InputProps={{ readOnly: true }}
                sx={fieldSx}
              />
              <TextField
                label={t('general.model', 'Model')}
                value={vehicle.model ?? vehicle.model_name ?? '-'}
                fullWidth
                variant="outlined"
                InputProps={{ readOnly: true }}
                sx={fieldSx}
              />
              <TextField
                label={t('masterVehicle.chasisNo', 'Chassis Number')}
                value={vehicle.chasis_no ?? '-'}
                fullWidth
                variant="outlined"
                InputProps={{ readOnly: true }}
                sx={fieldSx}
              />
              <TextField
                label={t('masterVehicle.fuelCapacity', 'Fuel Capacity')}
                value={vehicle.fuel_capacity ? `${vehicle.fuel_capacity} L` : '-'}
                fullWidth
                variant="outlined"
                InputProps={{ readOnly: true }}
                sx={fieldSx}
              />
              <TextField
                label={t('masterVehicle.fuelRatioKm', 'Fuel Ratio KM')}
                value={vehicle.fuel_ratio_km ? `${Number(vehicle.fuel_ratio_km).toFixed(2)} KM` : '-'}
                fullWidth
                variant="outlined"
                InputProps={{ readOnly: true }}
                sx={fieldSx}
              />
              <TextField
                label={t('masterVehicle.fuelRatioLiter', 'Fuel Ratio Liter')}
                value={vehicle.fuel_ratio_litre ? `${Number(vehicle.fuel_ratio_litre).toFixed(2)} L` : '-'}
                fullWidth
                variant="outlined"
                InputProps={{ readOnly: true }}
                sx={fieldSx}
              />
            </Stack>
          </Box>

          <Box sx={{ flex: '1 1 48%', minWidth: 250 }}>
            <Stack spacing={2}>
              <TextField
                label={t('general.pool', 'Pool')}
                value={vehicle.join?.pool_name ?? vehicle.pool_name ?? '-'}
                fullWidth
                variant="outlined"
                InputProps={{ readOnly: true }}
                sx={fieldSx}
              />
              <TextField
                label={t('masterVehicle.vendor', 'Vendor')}
                value={vehicle.vendor_name ?? vehicle.vendor ?? '-'}
                fullWidth
                variant="outlined"
                InputProps={{ readOnly: true }}
                sx={fieldSx}
              />
              <TextField
                label={t('general.year', 'Year')}
                value={vehicle.year ?? '-'}
                fullWidth
                variant="outlined"
                InputProps={{ readOnly: true }}
                sx={fieldSx}
              />
              <TextField
                label={t('masterVehicle.maxSpeed', 'Max Speed')}
                value={vehicle.max_speed ? `${vehicle.max_speed} km/h` : '-'}
                fullWidth
                variant="outlined"
                InputProps={{ readOnly: true }}
                sx={fieldSx}
              />
              <TextField
                label={t('masterVehicle.minSpeed', 'Min Speed')}
                value={vehicle.min_speed ? `${vehicle.min_speed} km/h` : '-'}
                fullWidth
                variant="outlined"
                InputProps={{ readOnly: true }}
                sx={fieldSx}
              />
              <TextField
                label={t('masterVehicle.fuelPrice', 'Fuel Price')}
                value={
                  vehicle.fuel_price_val != null && vehicle.fuel_price_val !== ''
                    ? `Rp ${Number(vehicle.fuel_price_val).toLocaleString('id-ID')}${vehicle.fuel_type_name ? ` (${vehicle.fuel_type_name})` : ''}`
                    : '-'
                }
                fullWidth
                variant="outlined"
                InputProps={{ readOnly: true }}
                sx={fieldSx}
              />

              <Box
                sx={{
                  mt: 1,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  gap: 3
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}>
                    QR PERTAMINA
                  </Typography>
                  <Box sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: '#fff' }}>
                    {qrPreview ? (
                      <Box component="img" src={qrPreview} sx={{ width: 100, height: 100, objectFit: 'contain' }} />
                    ) : (
                      <Box
                        sx={{
                          width: 100,
                          height: 100,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'grey.50'
                        }}
                      >
                        <Typography variant="caption" color="text.disabled">
                          {t('masterVehicle.noQr', 'No QR')}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* switches (kanan) */}
                <Box sx={{ display: 'box', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography>{t('masterVehicle.statusMaintenance', 'Status Maintenance')}</Typography>
                  <FormControlLabel
                    control={<Switch checked={Boolean(vehicle.is_maintenance)} disabled color="primary" />}
                    label={
                      <Typography sx={{ fontWeight: 600, color: vehicle.is_maintenance ? 'primary.main' : 'text.secondary' }}>
                        {vehicle.is_maintenance ? t('general.active', 'Active') : t('general.notActive', 'Not Active')}
                      </Typography>
                    }
                  />
                </Box>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>

      {/* Fuel Information Section */}
      <Divider sx={{ my: 1 }} />
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          {' '}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'start'
                }}
              >
                <StorageIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              </Box>
              <Typography variant="h6">{t('masterVehicle.fuelInformation', 'Fuel Information')}</Typography>
            </Box>
            <Tooltip title="View Fuel Price History">
              <IconButton size="small" onClick={() => setFuelHistoryDialogOpen(true)} color="primary">
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fafafa' }}>
            <Grid container spacing={2}>
              <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('masterVehicle.fuelTypeId', 'Fuel Type ID')}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {fuelTypeId || '-'}
                </Typography>
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('masterVehicle.fuelPriceId', 'Fuel Price ID')}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {vehicle.fuel_price || vehicle.fuel_price_id || '-'}
                </Typography>
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('masterVehicle.fuelType', 'Fuel Type Name')}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {vehicle.fuel_type_name || '-'}
                </Typography>
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('masterVehicle.fuelCode', 'Fuel Type Code')}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {vehicle.fuel_type_code || '-'}
                </Typography>
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('masterVehicle.fuelCategory', 'Fuel Category')}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {vehicle.fuel_type_category || '-'}
                </Typography>
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('masterVehicle.fuelUom', 'Fuel UOM')}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {vehicle.fuel_type_uom || '-'}
                </Typography>
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('masterVehicle.fuelPrice', 'Fuel Price')}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {vehicle.fuel_price_val != null && vehicle.fuel_price_val !== ''
                    ? 'Rp ' + Number(vehicle.fuel_price_val).toLocaleString('id-ID')
                    : '-'}
                </Typography>
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('masterVehicle.validAt', 'Valid At')}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {vehicle.fuel_price_valid_at &&
                  !String(vehicle.fuel_price_valid_at).startsWith('0001-01-01') &&
                  !String(vehicle.fuel_price_valid_at).startsWith('1900-01-01') &&
                  dayjs(vehicle.fuel_price_valid_at).isValid()
                    ? dayjs(vehicle.fuel_price_valid_at).format('DD MMM YYYY')
                    : '-'}
                </Typography>
              </Grid>
              <Grid item size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('masterVehicle.description', 'Description')}
                </Typography>
                <Typography variant="body2">{vehicle.fuel_type_desc || vehicle.fuel_price_desc || '-'}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start'
              }}
            >
              <StorageIcon sx={{ color: 'primary.main', fontSize: 28 }} />
            </Box>
            <Typography variant="h6">{t('masterVehicle.vehicleLicense', 'Vehicle License')}</Typography>
          </Box>
          {!vehicle.vehicle_license || vehicle.vehicle_license.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <InsertPhotoOutlinedIcon sx={{ fontSize: 64, color: 'grey.300' }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('masterVehicle.noLicense', 'No License')}
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {vehicle.vehicle_license.map((item, idx) => {
                const label =
                  item.join?.vehicle_license_license_name ||
                  item.license_name ||
                  item.license_type_label ||
                  t('general.unknown', 'Unknown Type');
                const expFormatted = item.license_exp_date ? item.license_exp_date.split('T')[0] : null;
                return (
                  <Grid item size={{ xs: 12, md: 12 }} key={`lic-${idx}`}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        display: 'flex',
                        gap: 2,
                        alignItems: 'center',
                        borderRadius: 2,
                        overflow: 'hidden'
                      }}
                    >
                      <Box sx={{ width: 84, height: 64, borderRadius: 1, overflow: 'hidden', flexShrink: 0 }}>
                        <Avatar
                          variant="rounded"
                          src={getImageUrl(item)}
                          sx={{
                            width: '100%',
                            height: '100%',
                            bgcolor: 'grey.100',
                            cursor: 'pointer',
                            p: 0
                          }}
                          onClick={() => handleOpenDetail(item)}
                        >
                          <InsertDriveFileIcon sx={{ fontSize: 32, color: '#004aad' }} />
                        </Avatar>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                          No: {item.license_no || '—'}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                          <Chip label={expFormatted ? `Exp: ${expFormatted}` : 'No Exp'} size="small" />
                        </Stack>
                      </Box>
                      <Box>
                        <Chip label={label} size="medium" variant="contained" color="info" />
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Grid>
      </Grid>

      <DetailDialog open={detailOpen} onClose={() => setDetailOpen(false)} title={detailContent.label} maxWidth="sm">
        <Box>
          {detailContent.type === 'photo' ? (
            <Grid container spacing={3} justifyContent="center" alignItems="flex-start">
              <Grid item size={{ xs: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', color: 'text.secondary' }}>
                  {t('masterVehicle.frontPhoto', 'FRONT PHOTO')}
                </Typography>
                <Box
                  sx={{
                    height: 180,
                    maxWidth: 300,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#0000',
                    overflow: 'hidden'
                  }}
                >
                  {detailContent.front ? (
                    <Box
                      component="img"
                      src={detailContent.front}
                      alt="Front"
                      sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <Stack alignItems="center" sx={{ minWidth: 300 }} spacing={1}>
                      <InsertPhotoOutlinedIcon sx={{ fontSize: 40, color: 'grey.400' }} />
                      <Typography variant="body2" color="text.disabled">
                        {t('warning.noImage', 'No Image')}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Grid>
              <Grid item size={{ xs: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', color: 'text.secondary' }}>
                  {t('masterVehicle.backPhoto', 'BACK PHOTO')}
                </Typography>
                <Box
                  sx={{
                    height: 180,
                    maxWidth: 300,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#0000',
                    overflow: 'hidden'
                  }}
                >
                  {detailContent.back ? (
                    <Box component="img" src={detailContent.back} alt="Back" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <Stack alignItems="center" sx={{ minWidth: 300 }} spacing={1}>
                      <InsertPhotoOutlinedIcon sx={{ fontSize: 40, color: 'grey.400' }} />
                      <Typography variant="body2" color="text.disabled">
                        {t('warning.noImage', 'No Image')}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Grid>
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <Paper
                variant="outlined"
                sx={{ height: 250, borderRadius: 2, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
              >
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                  <iframe
                    width="100%"
                    height="250px"
                    src={`https://docs.google.com/gview?url=${detailContent.document}&embedded=true`}
                    style={{ marginBottom: '8px' }}
                  />
                </Box>

                <Paper
                  variant="outlined"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    width: 250,
                    margin: '0 auto',
                    backgroundColor: '#f8f9fa',
                    borderStyle: 'dashed',
                    borderColor: '#1976d2',
                    p: 2
                  }}
                >
                  <Tooltip
                    title={
                      detailContent.document
                        ? decodeURIComponent(detailContent.document.split('?')[0].split('/').pop())
                        : 'Document_Vehicle.pdf'
                    }
                    arrow
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      noWrap
                      sx={{
                        maxWidth: '100%',
                        textAlign: 'center'
                      }}
                    >
                      {detailContent.document
                        ? decodeURIComponent(detailContent.document.split('?')[0].split('/').pop())
                        : 'Document_Vehicle.pdf'}
                    </Typography>
                  </Tooltip>
                  <Button type="button" startIcon={<CloudDownload />} component="label" size="small" variant="contained">
                    {t('masterVehicle.downloadFile', 'Download File')}
                  </Button>
                </Paper>
              </Paper>
            </Box>
          )}
        </Box>
      </DetailDialog>

      <FuelInformationDialog
        open={fuelHistoryDialogOpen}
        onClose={() => setFuelHistoryDialogOpen(false)}
        licensePlate={vehicle?.license_plate}
        vehicleId={vehicle?.id}
        vehicleName={vehicle?.name}
      />
    </Box>
  );
}
